// Bank opening evidence checks for the browser read model.
const bankUiNextIsoDay = (value) => {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

// Speiler `previousAdjacentStatement` i functions/bank/bankReconciliationMutationHttp.ts:
// forrige ledd i kjeden males i maneder, ikke i dager. Periodene kommer fra
// transaksjonsdatoene i filen, sa et krav om at forrige utskrift sluttet dognet
// for denne begynte slo aldri til over en helg. Svarer denne funksjonen noe annet
// enn backend, viser flaten «alt i orden» rett for backend nekter a bokfore.
const bankUiIsoMonth = (value) => String(value || '').slice(0, 7);

const bankUiShiftIsoMonth = (month, step) => {
  const parts = String(month || '').split('-').map(Number);
  if (!Number.isFinite(parts[0]) || !Number.isFinite(parts[1])) return month;
  return new Date(Date.UTC(parts[0], parts[1] - 1 + step, 1)).toISOString().slice(0, 7);
};

const bankUiStatementPeriodEnd = (statement) =>
  String(statement?.periodTo || statement?.periodFrom || '').slice(0, 10);

const bankStatementTransitionForUi = (state, statement) => {
  if ((statement.source || 'business') !== 'business') return statement;
  const account = safeArray(state?.settings?.bankAccounts).find((entry) => String(entry?.id || '') === String(statement.bankAccountId || ''));
  if (!account) return statement;
  // Klassiske Babel-skript deler globalt scope. Objekt-rest lager `_excluded`
  // paa toppnivaa og kolliderer med andre runtimefiler, saa feltene ryddes eksplisitt.
  const cleanStatement = { ...statement };
  delete cleanStatement.balanceTransitionStatus;
  delete cleanStatement.balanceTransitionCode;
  delete cleanStatement.balanceTransitionMessage;
  delete cleanStatement.expectedOpeningBalance;
  const reconciledThrough = String(account?.reconciledThrough || account?.startBalanceDate || '').slice(0, 10);
  const start = String(statement.periodFrom || statement.periodTo || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(reconciledThrough)) return cleanStatement;
  const firstLiveDate = bankUiNextIsoDay(reconciledThrough);
  const earliestDate = [start, ...safeArray(statement?.lines).map((line) => String(line?.date || '').slice(0, 10)).filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))].sort()[0] || start;
  const periodEnd = bankUiStatementPeriodEnd(statement);
  const configured = account?.startBalance === '' || account?.startBalance == null ? undefined : window.PlummeMoneyInput.parse(account.startBalance, { preferDecimal: true });
  const opening = statement.openingBalance ?? statement.openingBalanceAmount;
  const closing = statement.closingBalance ?? statement.closingBalanceAmount;
  const isEmptyDocumentaryTransition = reconciledThrough
    && periodEnd
    && periodEnd <= reconciledThrough
    && safeArray(statement?.lines).length === 0
    && Number.isFinite(Number(opening))
    && Number.isFinite(Number(closing))
    && Number.isFinite(configured)
    && Math.abs(Number(opening) - configured) < 0.005
    && Math.abs(Number(closing) - configured) < 0.005;
  if (isEmptyDocumentaryTransition) return cleanStatement;
  if (earliestDate < firstLiveDate) return {
    ...cleanStatement,
    balanceTransitionStatus: 'review',
    balanceTransitionCode: 'before-accounting-takeover',
    balanceTransitionMessage: `Bankutskriften inneholder bevegelser fra ${earliestDate}, men importert regnskap er satt som avstemt til og med ${reconciledThrough}. Linjene kan kontrolleres, men må ikke bokføres på nytt i Plumme.`,
  };
  const earliestNeighbourMonth = bankUiShiftIsoMonth(bankUiIsoMonth(start), -1);
  const previous = safeArray(state?.bankStatements)
    .filter((entry) => entry?.id !== statement.id && entry?.bankAccountId === statement.bankAccountId)
    .filter((entry) => bankUiStatementPeriodEnd(entry) && bankUiStatementPeriodEnd(entry) < start)
    .filter((entry) => !statement.periodImport || bankUiNextIsoDay(bankUiStatementPeriodEnd(entry)) === start)
    .filter((entry) => bankUiIsoMonth(bankUiStatementPeriodEnd(entry)) >= earliestNeighbourMonth)
    .sort((a, b) => bankUiStatementPeriodEnd(b).localeCompare(bankUiStatementPeriodEnd(a)))[0];
  const previousClosing = previous?.closingBalance ?? previous?.closingBalanceAmount;
  const expected = previous && previousClosing != null
    ? Number(previousClosing)
    : start === firstLiveDate && Number.isFinite(configured) ? configured : undefined;
  const actual = statement.openingBalance == null ? undefined : Number(statement.openingBalance);
  if (statement.periodImport && !Number.isFinite(actual)) return cleanStatement;
  if (Number.isFinite(expected) && (!Number.isFinite(actual) || Math.abs(actual - expected) >= 0.005)) return {
    ...cleanStatement,
    balanceTransitionStatus: 'review',
    balanceTransitionCode: actual == null ? 'missing-opening-balance' : 'opening-balance-mismatch',
    expectedOpeningBalance: expected,
    balanceTransitionMessage: Number.isFinite(actual)
      ? `Inngående saldo ${actual.toFixed(2)} stemmer ikke med forventet saldo ${expected.toFixed(2)} fra ${previous ? 'forrige bankutskrift' : 'importovergangen'}.`
      : `Bankutskriften mangler inngående saldo. Forventet saldo er ${expected.toFixed(2)} basert på ${previous ? 'forrige bankutskrift' : 'overgangen fra importert regnskap'}.`,
  };
  return cleanStatement;
};
